'use client'

import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { ExtendedHotelInfo } from '@/lib/backend-api'
import { SearchResultElement } from '@/components/ui/search/SearchResults'
import { Domain } from '../../../domain-config/domain-config'
import { formatHotelUrl, getHotelName } from '@/lib/utils/utils'
import Button from '@/components/common/button/Button'
import Stars from '@/components/common/stars'
import { ConfigContext } from '@/components/common/ConfigContext'
import { fetchSearchResult, SearchPageSearchResultItem } from '@/lib/frontent-api'
import { Picture } from '@/components/common/picture/Picture'
import { Sizes } from '@/components/common/picture/Sizes'
import { ButtonType } from '@/components/common/button/ButtonType'
import { useTranslations } from 'next-intl'

const SIZES: Record<Sizes, [number, number, number]> = {
  [Sizes.xs]: [140, 140, 140],
  [Sizes.sm]: [140, 140, 140],
  [Sizes.md]: [140, 140, 140],
  [Sizes.lg]: [140, 140, 140],
  [Sizes.xl]: [140, 140, 140],
}

function HotelDetailsModal(props: {
  domain: Domain
  hotel: ExtendedHotelInfo
  closeModal: () => void
}): ReactElement | null {
  const [hotelDetails, setHotelDetails] = useState<SearchPageSearchResultItem | undefined>()
  const config = useContext(ConfigContext)!

  useEffect(() => {
    fetchSearchResult(config.domain, config.currency, 1, { h: props.hotel.HotelCode })
      .then((r) => setHotelDetails(r.SearchResult.SearchResultItems[0]))
      .catch((e) => console.error(e))
  }, [])

  if (!hotelDetails) {
    return null
  }

  return (
    <SearchResultElement
      withinModal={true}
      domain={props.domain}
      hotelName={props.hotel.HotelName}
      hotel={hotelDetails}
      closeModal={props.closeModal}
    />
  )
}

export default function SliderHotelCard(props: {
  hotel: ExtendedHotelInfo
  domain: Domain
  setModal: (content: ReactElement | undefined) => void
}): ReactElement {
  const t = useTranslations('SearchPage')

  return (
    <>
      <div
        onClick={() =>
          props.setModal(
            <HotelDetailsModal
              domain={props.domain}
              hotel={props.hotel}
              closeModal={() => props.setModal(undefined)}
            />,
          )
        }
        className="border-box grid w-full grid-cols-[1fr,90px] grid-rows-[90px,auto] rounded-lg bg-white p-2.5 shadow-lg"
      >
        <div className="col-start-1 min-w-full">
          <div className="block max-h-[48px] overflow-hidden pr-2 text-sm font-semibold">
            {getHotelName(props.hotel.HotelName).join(' ')}
          </div>
          {!!props.hotel.RatingValue && (
            <div className="text-xs">
              <Stars rating={props.hotel.RatingValue} />
              <div>{props.hotel.RatingText}</div>
            </div>
          )}
        </div>
        <div className="relative z-10 col-start-2 row-start-1 rounded font-normal">
          <Picture
            src={props.hotel.Images[0]}
            alt={props.hotel.HotelName}
            size={SIZES}
            cornerRounding="rounded-lt-md"
          />
        </div>
        <div className="col-span-2 col-start-1 row-start-2 mt-4 flex justify-between border-t border-t-black-34 pt-2">
          <div className="ml-2 flex grow items-center justify-start text-xl">
            {props.hotel.PriceOriginal && props.hotel.PriceOriginal !== props.hotel.Price && (
              <div className="mr-3 mt-[2.5px]  text-[#d7194f] line-through decoration-[#d7194f] decoration-2">
                {props.hotel.PriceString}
              </div>
            )}
            <div className="text-2xl font-semibold text-stone-800 lg:hidden xl:block">
              {props.hotel.PriceLine2}
            </div>
          </div>
          <div className="col-start-2 row-start-2">
            <Button
              buttonType={ButtonType.PRIMARY}
              className="whitespace-nowrap text-white"
              onClick={() => window.open(formatHotelUrl(props.hotel.Url), '_blank')}
            >
              <span className="hidden md:inline">{t('mapSliderButtonLabel')}</span>
              <span className="md:hidden">{t('mapSliderButtonLabelSmall')}</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}
